import { useNavigation, useRoute } from '@react-navigation/native';
import get from 'lodash.get';
import sortBy from 'lodash.sortby';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { FlatList, ScrollView, TouchableOpacity } from 'react-native';
import { useToast } from 'react-native-toast-notifications';

import { AddMemberType } from '@components/Invite/SelectMembersList';
import { CancelInviteMemberConfirmModal } from '@components/Modals/CancelInviteMemberConfirmModal';
import { WebModalV3 } from '@components/Modals/WebModalV3.web';
import { MembersFilterOverlay } from '@components/Profile/TeamDetailPage/MembersFilterOverlay.web';
import { MessageMemberOverlay } from '@components/Profile/TeamDetailPage/MessageMemberOverlay.web';
import { ProjectInviteOrAddMember } from '@components/Projects/ProjectInviteOrAddMember.web';
import { Box, Text } from '@components/Restyle';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import { Skeleton } from '@components/shared/Skeleton';
import { MemberRowSkeleton, SkeletonList } from '@components/shared/skeletons';
import { FileDeletedToast } from '@components/TaskList/FileDeletedToast.web';
import { SimpleHoverable } from '@components/TaskList/SimpleHoverable';
import { TooltipWebV3 } from '@components/TaskList/TooltipWebV3.web';
import { ProjectMemberRow } from '@components/User/ProjectMemberRow.web';
import {
  ChatDrawerScreen,
  useWebDrawer,
} from '@components/Web/Drawer/WebDrawerContext';
import {
  ProjectMemberRole,
  User,
  useCreateChatMutation,
  useCreateProjectInvitationMutation,
  useDeleteProjectInvitationMutation,
  useDeleteUserRequestMutation,
  useListContactUsersQuery,
  useRemoveProjectMembersMutation,
  useResendProjectInvitationMutation,
  useUpdateProjectMembersMutation,
} from '@graphql/generated';
import useActiveChat from '@hooks/useActiveChat';
import useMe from '@hooks/useMe';
import { useOutsideClick } from '@hooks/useOutsideClick.web';
import { useProjectFromQuery } from '@hooks/useProjectFromQuery';
import { useProjectMembership } from '@hooks/useProjectMembership';
import useSearch from '@hooks/useSearch';
import { ProjectDetailTabsScreenProps } from '@navigation/projects/project-detail-tabs.web';
import { useAppNavigation } from '@navigation/useAppNavigation';
import theme from '@themes/theme';
import {
  canChangeProjectMemberRole,
  canRemoveProjectMember,
  canUpdateAdminRole,
  getProjectRoleEnumValue,
  selectableProjectRoles,
} from '@utils/projects';

interface ProjectMembersProps {
  externalShowAddMembersModal?: boolean;
  onExternalModalClose?: () => void;
  /** Opens the invite modal from inside the tab (e.g. the Pending empty state). */
  onInvitePress?: () => void;
  onInviteSent?: () => void;
  initialInviteEmails?: {
    email: string;
    id?: string;
    name?: string;
    avatarId?: string;
  }[];
}

const projectMemberFilterOptions = ['admin', 'editor', 'viewer'] as const;

// Each active-member row owns its hover state locally so the highlight does not
// depend on the parent re-rendering / FlatList recycling the cell — that timing
// was unreliable on fast mouse movement and caused the hover bg to occasionally
// not apply. `raised` lifts the row above siblings while its role dropdown is open.
const MemberRowWrapper: React.FC<{
  raised: boolean;
  children: React.ReactNode;
}> = ({ raised, children }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <Box
      paddingHorizontal='m'
      paddingVertical='xs'
      backgroundColor={hovered ? 'grey01' : 'white'}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative' as const,
        zIndex: raised ? 10000 : 0,
      }}>
      {children}
    </Box>
  );
};

// Pending-invite rows are plain flex rows (no role dropdown), so they keep their
// own local hover state — same reasoning as MemberRowWrapper: a self-contained
// hover avoids depending on FlatList re-rendering the recycled cell.
const PendingInviteRow: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [hovered, setHovered] = useState(false);
  return (
    <Box
      paddingHorizontal='m'
      paddingVertical='xs'
      flexDirection='row'
      alignItems='center'
      backgroundColor={hovered ? 'grey01' : 'white'}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}>
      {children}
    </Box>
  );
};

export const ProjectMembers: React.FC<ProjectMembersProps> = ({
  externalShowAddMembersModal,
  onInvitePress,
  onExternalModalClose,
  onInviteSent,
  initialInviteEmails,
}) => {
  const { t } = useTranslation('models');
  const toast = useToast();
  const { me } = useMe();
  const appNavigation = useAppNavigation();
  const { chatListOpen, chatExpanded, setChatListOpen, setShouldChatListOpen } =
    useWebDrawer();
  const { setIsOpenChat, setActiveMessageId } = useActiveChat();
  const [isSortName, setSortName] = useState(false);
  const [isSortNameAsc, setSortNameAsc] = useState(false);
  const [isSortDateSent, setSortDateSent] = useState(false);
  const [isSortDateSentAsc, setSortDateSentAsc] = useState(false);
  const [isActiveExpanded, setIsActiveExpanded] = useState(true);
  const [isPendingExpanded, setIsPendingExpanded] = useState(true);
  const [openDropdownMemberId, setOpenDropdownMemberId] = useState<
    string | null
  >(null);

  // Filter state
  const [showFilterOverlay, setShowFilterOverlay] = useState(false);
  const [filterSearch, setFilterSearch] = useState('');
  const [filterRole, setFilterRole] = useState<
    'admin' | 'editor' | 'viewer' | null
  >(null);
  const filterButtonRef = useRef<HTMLDivElement>(null);
  const filterOverlayRef = useRef<HTMLDivElement>(null);

  // Start a Chat state
  const [showMessageMemberOverlay, setShowMessageMemberOverlay] =
    useState(false);
  const [messageMemberSearch, setMessageMemberSearch] = useState('');
  const [selectedMembersForChat, setSelectedMembersForChat] = useState<
    string[]
  >([]);
  const messageMemberButtonRef = useRef<HTMLDivElement>(null);
  const messageMemberOverlayRef = useRef<HTMLDivElement>(null);

  const route = useRoute<ProjectDetailTabsScreenProps<'members'>['route']>();
  const { projectUUId, openAddMembers } = route.params;
  const navigation = useNavigation();
  const { search } = useSearch();
  const { project, loading } = useProjectFromQuery({
    projectUUId,
  });

  const projectMembers = project?.members || [];
  const projectInvitations = project?.invitations || [];
  const projectUsers = project?.userRequests || [];

  // TA-10502: lifted out of ProjectMemberRow — one listContactUsers request per
  // screen instead of one per rendered row.
  const { data: listContactsData } = useListContactUsersQuery({
    variables: { term: '' },
  });
  const contactIds = useMemo(
    () =>
      new Set(
        listContactsData?.listContactUsers?.map((contact) => contact.id) || []
      ),
    [listContactsData]
  );

  // Sort invitations by date sent (expiredAt is used as a proxy for date sent)
  const sortedInvitations = isSortDateSent
    ? [...projectInvitations].sort((a, b) => {
        const dateA = a.expiredAt ? new Date(a.expiredAt).getTime() : 0;
        const dateB = b.expiredAt ? new Date(b.expiredAt).getTime() : 0;
        return isSortDateSentAsc ? dateA - dateB : dateB - dateA;
      })
    : projectInvitations;

  // Helper to format role (e.g., "VIEWER" -> "Viewer")
  const formatRole = (role: string | null | undefined) => {
    if (!role) return 'Viewer';
    return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
  };
  const { isEditorOrHigher } = useProjectMembership(project);

  const alphabetizedMembers = sortBy(projectMembers, [
    (item) => get(item, 'user.firstName')?.toLowerCase(),
    (item) => get(item, 'user.lastName')?.toLowerCase(),
  ]);

  const alphabetizedMembers1 = isSortNameAsc
    ? alphabetizedMembers
    : alphabetizedMembers.toReversed();

  const sortedMembers = isSortName
    ? [...alphabetizedMembers1]
    : [...projectMembers]?.sort((a) =>
        a.role === ProjectMemberRole.Owner ? -1 : 1
      );

  // Filter active members based on filter criteria
  const filteredActiveMembers = useMemo(() => {
    let filtered = sortedMembers;

    // Apply role filter
    if (filterRole === 'admin') {
      filtered = filtered.filter(
        (m) =>
          m.role === ProjectMemberRole.Admin ||
          m.role === ProjectMemberRole.Owner
      );
    } else if (filterRole === 'editor') {
      filtered = filtered.filter((m) => m.role === ProjectMemberRole.Editor);
    } else if (filterRole === 'viewer') {
      filtered = filtered.filter((m) => m.role === ProjectMemberRole.Viewer);
    }

    // Apply search filter
    if (filterSearch.trim()) {
      const searchLower = filterSearch.toLowerCase().trim();
      filtered = filtered.filter((m) => {
        const name =
          m.user.name ||
          `${m.user.firstName || ''} ${m.user.lastName || ''}`.trim();
        return (
          name.toLowerCase().includes(searchLower) ||
          m.user.email?.toLowerCase().includes(searchLower) ||
          m.user.phoneNumber?.includes(searchLower)
        );
      });
    }

    return filtered;
  }, [sortedMembers, filterRole, filterSearch]);

  // Filter pending invitations based on filter criteria
  const filteredInvitations = useMemo(() => {
    let filtered = sortedInvitations;

    // Apply role filter
    if (filterRole === 'admin') {
      filtered = filtered.filter((inv) => inv.role?.toLowerCase() === 'admin');
    } else if (filterRole === 'editor') {
      filtered = filtered.filter((inv) => inv.role?.toLowerCase() === 'editor');
    } else if (filterRole === 'viewer') {
      filtered = filtered.filter((inv) => inv.role?.toLowerCase() === 'viewer');
    }

    // Apply search filter
    if (filterSearch.trim()) {
      const searchLower = filterSearch.toLowerCase().trim();
      filtered = filtered.filter((inv) => {
        return inv.email?.toLowerCase().includes(searchLower);
      });
    }

    return filtered;
  }, [sortedInvitations, filterRole, filterSearch]);

  // Filter user requests based on filter criteria
  const filteredUserRequests = useMemo(() => {
    let filtered = projectUsers;

    // Apply search filter
    if (filterSearch.trim()) {
      const searchLower = filterSearch.toLowerCase().trim();
      filtered = filtered.filter((item) => {
        const userName = item?.user?.name || '';
        return userName.toLowerCase().includes(searchLower);
      });
    }

    return filtered;
  }, [projectUsers, filterSearch]);

  const [createChat, { data: createChatData }] = useCreateChatMutation();

  useEffect(() => {
    if (!createChatData) return;
    if (!chatExpanded && chatListOpen) {
      setShouldChatListOpen(true);
      setChatListOpen(false);
    }
    setIsOpenChat(true);
    setActiveMessageId?.(undefined);
    appNavigation.navigateToChatDrawer({
      screen: ChatDrawerScreen.details,
      activeChat: createChatData.createChat,
    });
    setShowMessageMemberOverlay(false);
    setSelectedMembersForChat([]);
    setMessageMemberSearch('');
  }, [createChatData]);

  // Get all team member users for chat
  const allProjectMemberUsers: User[] = useMemo(() => {
    return projectMembers.map((m) => m.user);
  }, [projectMembers]);

  // Filtered members for chat overlay (search filter)
  const filteredMembersForChat: User[] = useMemo(() => {
    if (!messageMemberSearch) return allProjectMemberUsers;
    const searchLower = messageMemberSearch.toLowerCase().trim();
    return allProjectMemberUsers.filter((user) => {
      const name =
        user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim();
      return name.toLowerCase().includes(searchLower);
    });
  }, [allProjectMemberUsers, messageMemberSearch]);

  // Filter overlay outside click handling
  const handleFilterOutsideClick = useCallback((event: MouseEvent) => {
    const isClickInOverlay = filterOverlayRef.current?.contains(
      event.target as Node
    );
    const isClickInButton = filterButtonRef.current?.contains(
      event.target as Node
    );
    if (!isClickInOverlay && !isClickInButton) {
      setShowFilterOverlay(false);
    }
  }, []);

  // Message member overlay outside click handling
  const handleMessageMemberOutsideClick = useCallback((event: MouseEvent) => {
    const isClickInOverlay = messageMemberOverlayRef.current?.contains(
      event.target as Node
    );
    const isClickInButton = messageMemberButtonRef.current?.contains(
      event.target as Node
    );
    if (!isClickInOverlay && !isClickInButton) {
      setShowMessageMemberOverlay(false);
    }
  }, []);

  const filterOutsideClickRef = useOutsideClick<HTMLDivElement>(
    handleFilterOutsideClick
  );
  const messageMemberOutsideClickRef = useOutsideClick<HTMLDivElement>(
    handleMessageMemberOutsideClick
  );

  // Handle creating a chat from selected members
  const handleCreateChat = useCallback(() => {
    const membersToChat = selectedMembersForChat.filter((id) => id !== me?.id);
    if (membersToChat.length === 0) return;

    createChat({
      variables: {
        attributes: {
          userIds: membersToChat,
          groupChat: membersToChat.length > 1,
        },
      },
    });
  }, [selectedMembersForChat, me?.id, createChat]);

  // Clear all filters
  const handleClearFilters = useCallback(() => {
    setFilterSearch('');
    setFilterRole(null);
  }, []);

  // Filter count for badge
  const filterCount = useMemo(() => {
    let count = 0;
    if (filterSearch) count += 1;
    if (filterRole) count += 1;
    return count;
  }, [filterSearch, filterRole]);

  // Toggle member selection for chat
  const toggleMemberSelection = useCallback((userId: string) => {
    setSelectedMembersForChat((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  }, []);

  // Toggle all members selection for chat
  const toggleAllMembersSelection = useCallback(() => {
    const allIds = filteredMembersForChat
      .filter((m) => m.id !== me?.id)
      .map((m) => m.id);
    const allSelected = allIds.every((id) =>
      selectedMembersForChat.includes(id)
    );
    if (allSelected) {
      setSelectedMembersForChat([]);
    } else {
      setSelectedMembersForChat(allIds);
    }
  }, [filteredMembersForChat, selectedMembersForChat, me]);

  // Get filter overlay position
  const getFilterOverlayPosition = useCallback(() => {
    if (!filterButtonRef.current) return undefined;
    const rect = filterButtonRef.current.getBoundingClientRect();
    return {
      top: rect.bottom + 8,
      left: rect.left,
    };
  }, []);

  // Get message member overlay position
  const getMessageMemberOverlayPosition = useCallback(() => {
    if (!messageMemberButtonRef.current) return undefined;
    const rect = messageMemberButtonRef.current.getBoundingClientRect();
    return {
      top: rect.bottom + 8,
      left: rect.left,
    };
  }, []);

  const [localShowAddMembersModal, setLocalShowAddMembersModal] =
    useState(false);

  // State for delete invitation confirmation modal
  const [showDeleteInviteModal, setShowDeleteInviteModal] = useState(false);
  const [invitationToDelete, setInvitationToDelete] = useState<{
    id: string;
    displayName: string;
    type: 'projectInvitation' | 'userRequest';
  } | null>(null);

  // Combine external and local modal state - external takes precedence when provided
  const showAddMembersModal =
    externalShowAddMembersModal !== undefined
      ? externalShowAddMembersModal
      : localShowAddMembersModal;

  const handleCloseModal = () => {
    if (onExternalModalClose) {
      onExternalModalClose();
    } else {
      setLocalShowAddMembersModal(false);
    }
  };

  useEffect(() => {
    if (openAddMembers === true || openAddMembers === 'true') {
      if (onExternalModalClose) {
        // If external control is provided, we don't need to do anything here
        // The parent should handle opening the modal
      } else {
        setLocalShowAddMembersModal(true);
      }
      navigation.setParams({ openAddMembers: undefined });
    }
  }, [openAddMembers, navigation, onExternalModalClose]);

  const [updateProjectMembers] = useUpdateProjectMembersMutation({
    refetchQueries: [
      'listProjects',
      'listActivitiesByProject',
      'listActivities',
    ],
  });

  const [removeProjectMember] = useRemoveProjectMembersMutation({
    refetchQueries: [
      'listProjects',
      'listActivitiesByProject',
      'listActivities',
    ],
  });

  const [deleteProjectInvitation, { loading: isDeletingProjectInvitation }] =
    useDeleteProjectInvitationMutation({
      refetchQueries: ['listProjects'],
      update: (cache, _result, { variables }) => {
        // Immediately remove the deleted invitation from the Apollo cache
        // so the UI updates without waiting for the refetch.
        // Note: We intentionally do NOT refetch 'getProject' here because
        // doing so causes the page to show a loading spinner and go blank.
        // The cache eviction + gc is sufficient to remove the invitation
        // from the UI since Apollo filters out dangling references.
        if (variables?.projectInvitationId) {
          cache.evict({
            id: cache.identify({
              __typename: 'ProjectInvitation',
              id: variables.projectInvitationId,
            }),
          });
          cache.gc();
        }
      },
    });

  const [deleteUserRequest, { loading: isDeletingUserRequest }] =
    useDeleteUserRequestMutation({
      refetchQueries: ['listProjects'],
      update: (cache, _result, { variables }) => {
        if (variables?.id) {
          cache.evict({
            id: cache.identify({
              __typename: 'UserRequest',
              id: variables.id,
            }),
          });
          cache.gc();
        }
      },
    });

  const [createProjectInvitation] = useCreateProjectInvitationMutation({
    refetchQueries: ['getProject', 'listProjects'],
  });

  const [resendProjectInvitation] = useResendProjectInvitationMutation({
    refetchQueries: ['getProject', 'listProjects'],
  });

  const webToastSuccess = (title: string, width = 240) => {
    toast.show(
      <FileDeletedToast title={title} duration={3000} width={width} />,
      {
        type: 'custom',
        duration: 3000,
        placement: 'bottom',
        animationType: 'slide-in',
        style: {
          backgroundColor: 'transparent',
          shadowColor: 'transparent',
          padding: theme.spacing.none,
          margin: theme.spacing.none,
          width,
          height: theme.spacing.listFooter,
        },
      }
    );
  };

  const handleResendInvitation = async (invitationId: string) => {
    try {
      await resendProjectInvitation({
        variables: {
          projectInvitationId: invitationId,
        },
      });
      webToastSuccess(t('projects.members.invitationResent'));
    } catch {
      // Error is handled by Apollo Client
    }
  };

  const handleDeleteInvitation = (invitation: {
    id: string;
    displayName: string;
    type: 'projectInvitation' | 'userRequest';
  }) => {
    setInvitationToDelete(invitation);
    setShowDeleteInviteModal(true);
  };

  const isCancelingInvite =
    isDeletingUserRequest || isDeletingProjectInvitation;

  const confirmDeleteInvitation = async () => {
    if (invitationToDelete) {
      try {
        if (invitationToDelete.type === 'userRequest') {
          await deleteUserRequest({
            variables: { id: invitationToDelete.id },
          });
        } else {
          await deleteProjectInvitation({
            variables: {
              projectInvitationId: invitationToDelete.id,
            },
          });
        }
        webToastSuccess(t('projects.members.inviteDeleted'));
        setShowDeleteInviteModal(false);
        setInvitationToDelete(null);
      } catch {
        // Deliberately leave the modal open on failure (global spinner rule:
        // "restore button + inline error") — closing here would silently
        // discard the user's cancel-invite intent.
        toast.show(t('shared:deleteFailedTryAgain'), { duration: 2000 });
      }
    }
  };

  const removeMemberFromProject = (memberId: string) => {
    if (project) {
      const { id, color, name, teamId, members } = project;
      // TODO: this query should be scoped to only require necessary data to update project members

      const users = projectMembers.filter((item) => item.user.id !== memberId);

      removeProjectMember({
        variables: {
          id,
          attributes: {
            color,
            name,
            teamId,
            members: users.map((u) => {
              return {
                userId: u.user.id,
                role: u.role,
              };
            }),
          },
        },
        optimisticResponse: {
          updateProject: {
            ...project,
            members,
            usersCount: users.length,
          },
        },
      });
    }
  };

  const canUpdateMemberRole = (member: (typeof projectMembers)[number]) =>
    canChangeProjectMemberRole(
      project?.currentUserRole,
      project?.currentUserTeamRole
    ) &&
    member.role !== ProjectMemberRole.Owner &&
    (member.role !== ProjectMemberRole.Admin ||
      canUpdateAdminRole(
        project?.currentUserRole,
        project?.currentUserTeamRole
      ));

  // Cancelling/resending a pending invite is reserved for Project Owner/Admin
  // or Team Owner/Admin — they may cancel ANY pending invite, not just their
  // own. Project Editors (and lower) cannot. Same predicate as role change.
  const canCancelInvite = canChangeProjectMemberRole(
    project?.currentUserRole,
    project?.currentUserTeamRole
  );

  // Admin is omitted from the role picker when the current user lacks permission
  // to assign it (Project Owner / Team Owner only).
  const assignableRoles = useMemo(
    () =>
      canUpdateAdminRole(project?.currentUserRole, project?.currentUserTeamRole)
        ? selectableProjectRoles
        : selectableProjectRoles.filter((r) => r !== ProjectMemberRole.Admin),
    [project?.currentUserRole, project?.currentUserTeamRole]
  );

  const updateProjectMember = async (
    userId: User['id'],
    role: ProjectMemberRole
  ) => {
    if (project) {
      const { id } = project;
      try {
        const result = await updateProjectMembers({
          variables: {
            id,
            attributes: [{ userId, role: getProjectRoleEnumValue(role) }],
          },
        });
        if (result.errors?.length) {
          toast.show(t('shared:updateFailedTryAgain'), { duration: 2000 });
          return;
        }
        webToastSuccess(t('addMembers.roleUpdated'));
      } catch {
        toast.show(t('shared:updateFailedTryAgain'), { duration: 2000 });
      }
    }
  };

  // `loading` alone would also be true during a background refetch (e.g.
  // createProjectInvitation/resendProjectInvitation's refetchQueries includes
  // 'getProject') even though `project` is already loaded — gating on
  // `!project` too keeps the already-loaded members list visible instead of
  // wiping it back to a skeleton mid-refetch (PR #5566 review).
  if (loading && !project) {
    return (
      <Box flex={1} padding='m'>
        <Box
          flexDirection='row'
          style={{ gap: theme.spacing.s }}
          marginBottom='m'>
          <Skeleton variant='rect' width={140} height={32} borderRadius='s' />
          <Skeleton variant='rect' width={100} height={32} borderRadius='s' />
        </Box>
        <SkeletonList row={(index) => <MemberRowSkeleton key={index} />} />
      </Box>
    );
  }

  if (!loading && !project) {
    return (
      <Box flex={1} paddingTop='m' alignItems='center'>
        <Text>Project not found</Text>
      </Box>
    );
  }

  const getActiveHeader = () => {
    return (
      <Box
        flexDirection='row'
        alignItems='center'
        paddingHorizontal='m'
        paddingVertical='xs'
        backgroundColor='grey01'>
        <Box flex={1.2} flexDirection='row' alignItems='center'>
          <Text variant='webBodySecondary' color='grey04'>
            {t('projects.members.name')}
          </Text>
          <Icon
            marginLeft='xs'
            variant='xxs'
            name='ArrowUpNarrow'
            color={isSortName && isSortNameAsc ? 'greenSecondary' : 'grey04'}
            style={{ marginRight: -3 }}
            onPress={() => {
              if (isSortName && isSortNameAsc) {
                setSortName(false);
                setSortNameAsc(false);
              } else {
                setSortName(true);
                setSortNameAsc(true);
              }
            }}
          />
          <Icon
            variant='xxs'
            name='ArrowDownNarrow'
            color={isSortName && !isSortNameAsc ? 'greenSecondary' : 'grey04'}
            style={{ marginLeft: -3 }}
            onPress={() => {
              if (isSortName && !isSortNameAsc) {
                setSortName(false);
                setSortNameAsc(false);
              } else {
                setSortName(true);
                setSortNameAsc(false);
              }
            }}
          />
        </Box>
        <Box flex={1} flexDirection='row' justifyContent='flex-start'>
          <Text variant='webBodySecondary' color='grey04'>
            {t('projects.members.skills')}
          </Text>
        </Box>
        <Box flex={1.2} flexDirection='row' justifyContent='flex-start'>
          <Text variant='webBodySecondary' color='grey04'>
            {t('projects.members.email')}
          </Text>
        </Box>
        <Box flex={0.8} flexDirection='row' justifyContent='flex-start'>
          <Text variant='webBodySecondary' color='grey04'>
            {t('projects.members.phone')}
          </Text>
        </Box>
        <Box
          flex={0.5}
          flexDirection='row'
          justifyContent='flex-start'
          paddingLeft='s'>
          <Text variant='webBodySecondary' color='grey04'>
            {t('projects.members.status')}
          </Text>
        </Box>
        <Box width={theme.spacing.xl + theme.spacing.xxs} />
        <Box width={theme.spacing.xl + theme.spacing.xxs} />
      </Box>
    );
  };

  const getPendingHeader = () => {
    return (
      <Box
        flexDirection='row'
        alignItems='center'
        paddingHorizontal='m'
        paddingVertical='xs'
        backgroundColor='grey01'>
        <Box flex={1.2} flexDirection='row' alignItems='center'>
          <Text variant='webBodySecondary' color='grey04'>
            {t('projects.members.emailOrName')}
          </Text>
        </Box>
        <Box flex={0.8} flexDirection='row' justifyContent='flex-start'>
          <Text variant='webBodySecondary' color='grey04'>
            {t('projects.members.invitedBy')}
          </Text>
        </Box>
        <Box flex={0.8} flexDirection='row' alignItems='center'>
          <Text variant='webBodySecondary' color='grey04'>
            {t('projects.members.dateSent')}
          </Text>
          <Icon
            marginLeft='xs'
            variant='xxs'
            name='ArrowUpNarrow'
            color={
              isSortDateSent && isSortDateSentAsc ? 'greenSecondary' : 'grey04'
            }
            style={{ marginRight: -3 }}
            onPress={() => {
              if (isSortDateSent && isSortDateSentAsc) {
                setSortDateSent(false);
                setSortDateSentAsc(false);
              } else {
                setSortDateSent(true);
                setSortDateSentAsc(true);
              }
            }}
          />
          <Icon
            variant='xxs'
            name='ArrowDownNarrow'
            color={
              isSortDateSent && !isSortDateSentAsc ? 'greenSecondary' : 'grey04'
            }
            style={{ marginLeft: -3 }}
            onPress={() => {
              if (isSortDateSent && !isSortDateSentAsc) {
                setSortDateSent(false);
                setSortDateSentAsc(false);
              } else {
                setSortDateSent(true);
                setSortDateSentAsc(false);
              }
            }}
          />
        </Box>
        <Box flex={0.8} flexDirection='row' justifyContent='flex-start'>
          <Text variant='webBodySecondary' color='grey04'>
            {t('projects.members.expirationDate')}
          </Text>
        </Box>
        <Box flex={0.5} flexDirection='row' justifyContent='flex-start'>
          <Text variant='webBodySecondary' color='grey04'>
            {t('projects.members.role')}
          </Text>
        </Box>
        <Box flex={0.5} flexDirection='row' justifyContent='center'>
          <Text variant='webBodySecondary' color='grey04'>
            {t('projects.members.action')}
          </Text>
        </Box>
      </Box>
    );
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const renderSectionHeader = (
    title: string,
    count: number,
    isExpanded: boolean,
    onToggle: () => void
  ) => {
    return (
      <TouchableOpacity onPress={onToggle}>
        <Box
          flexDirection='row'
          alignItems='center'
          justifyContent='space-between'
          paddingHorizontal='m'
          paddingVertical='s'>
          <Box flexDirection='row' alignItems='center'>
            <Text variant='labelEmphasized' color='textPrimary'>
              {title}
            </Text>
            {count > 0 && (
              <Box
                marginLeft='xs'
                minWidth={20}
                height={20}
                paddingHorizontal='xxs'
                alignItems='center'
                justifyContent='center'
                borderRadius='s'
                borderWidth={1}
                borderColor='grey02'
                backgroundColor='white'>
                <Text variant='metadata1' color='textSecondary'>
                  {count}
                </Text>
              </Box>
            )}
          </Box>
          <Icon
            name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
            variant='s'
            color='textSecondary'
          />
        </Box>
      </TouchableOpacity>
    );
  };

  const pendingCount = filteredInvitations.length + filteredUserRequests.length;
  const activeCount = filteredActiveMembers.length;

  return (
    <>
      <ScrollView
        style={{ flex: 1 }}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{
          paddingBottom: theme.spacing.xl,
          paddingHorizontal: theme.spacing.m,
        }}>
        <Box flexDirection='row' alignItems='center' marginTop='xs'>
          <Box ref={filterButtonRef} marginRight='xs' flexDirection='row'>
            <SimpleHoverable
              onPress={() => {
                setShowMessageMemberOverlay(false);
                setShowFilterOverlay(!showFilterOverlay);
              }}
              accessibilityLabel='Filter members'>
              {(isHovered) => (
                <Box
                  flexDirection='row'
                  alignItems='center'
                  paddingHorizontal='m'
                  borderRadius='xs'
                  maxHeight={36}
                  height={36}
                  marginLeft='xs'
                  backgroundColor={
                    isHovered || showFilterOverlay || filterCount > 0
                      ? 'grey01'
                      : 'white'
                  }>
                  <Icon
                    name='AlignCenterDesc'
                    color='textPrimary'
                    variant='s'
                    style={{ marginRight: theme.spacing.xxs }}
                  />
                  <Text variant='webBodySecondary' color='textPrimary'>
                    Filter
                  </Text>
                  {filterCount > 0 && (
                    <Box
                      backgroundColor='greenSecondary'
                      borderRadius='xl'
                      marginLeft='xxs'
                      width={18}
                      height={18}
                      alignItems='center'
                      justifyContent='center'>
                      <Text
                        variant='metadataSecondary'
                        color='white'
                        textAlign='center'>
                        {filterCount}
                      </Text>
                    </Box>
                  )}
                </Box>
              )}
            </SimpleHoverable>
          </Box>

          <Box ref={messageMemberButtonRef}>
            <SimpleHoverable
              onPress={() => {
                setShowFilterOverlay(false);
                setShowMessageMemberOverlay(!showMessageMemberOverlay);
              }}
              accessibilityLabel='Start Chat'>
              {(isHovered) => (
                <Box
                  flexDirection='row'
                  alignItems='center'
                  paddingHorizontal='m'
                  borderRadius='xs'
                  maxHeight={36}
                  height={36}
                  backgroundColor={
                    isHovered || showMessageMemberOverlay ? 'grey01' : 'white'
                  }>
                  <Icon
                    name='MessageSquare'
                    color='textPrimary'
                    variant='s'
                    style={{ marginRight: theme.spacing.xxs }}
                  />
                  <Text variant='webBodySecondary' color='textPrimary'>
                    Start a Chat
                  </Text>
                </Box>
              )}
            </SimpleHoverable>
          </Box>
        </Box>

        <MembersFilterOverlay
          visible={showFilterOverlay}
          searchValue={filterSearch}
          roleOptions={projectMemberFilterOptions}
          selectedRole={filterRole}
          onClose={() => setShowFilterOverlay(false)}
          onSearchChange={setFilterSearch}
          onRoleChange={setFilterRole}
          onClearAll={handleClearFilters}
          overlayRef={filterOverlayRef}
          outsideClickRef={filterOutsideClickRef}
          position={getFilterOverlayPosition()}
        />

        <MessageMemberOverlay
          visible={showMessageMemberOverlay}
          onClose={() => setShowMessageMemberOverlay(false)}
          searchValue={messageMemberSearch}
          onSearchChange={setMessageMemberSearch}
          filteredMembers={filteredMembersForChat}
          selectedMembers={selectedMembersForChat}
          onToggleMemberSelection={toggleMemberSelection}
          onToggleAllMembersSelection={toggleAllMembersSelection}
          onCreateChat={handleCreateChat}
          overlayRef={messageMemberOverlayRef}
          outsideClickRef={messageMemberOutsideClickRef}
          position={getMessageMemberOverlayPosition()}
        />

        <Box
          marginTop='m'
          borderWidth={1}
          borderColor='grey02'
          borderRadius='s'
          overflow='hidden'>
          {renderSectionHeader(
            t('projects.members.active'),
            activeCount,
            isActiveExpanded,
            () => setIsActiveExpanded(!isActiveExpanded)
          )}
          {isActiveExpanded && (
            <>
              {getActiveHeader()}
              <FlatList
                showsVerticalScrollIndicator={true}
                data={filteredActiveMembers}
                extraData={openDropdownMemberId}
                style={{ maxHeight: 300 }}
                renderItem={({ item }) => (
                  <MemberRowWrapper
                    raised={openDropdownMemberId === item.user.id}>
                    <ProjectMemberRow<ProjectMemberRole>
                      roles={assignableRoles}
                      member={item}
                      updateRole={
                        canUpdateMemberRole(item)
                          ? (i) => updateProjectMember(item.user.id, i)
                          : undefined
                      }
                      removeMember={
                        canRemoveProjectMember(
                          project?.currentUserRole,
                          project?.currentUserTeamRole,
                          item.role
                        )
                          ? (member) => removeMemberFromProject(member.user.id)
                          : undefined
                      }
                      filterVal={search}
                      isContact={contactIds.has(item.user.id)}
                      isDropdownOpen={openDropdownMemberId === item.user.id}
                      onDropdownToggle={(isOpen) =>
                        setOpenDropdownMemberId(isOpen ? item.user.id : null)
                      }
                    />
                  </MemberRowWrapper>
                )}
                keyExtractor={(item) => item.user.id.toString()}
                ItemSeparatorComponent={() => (
                  <Box height={1} backgroundColor='grey02' />
                )}
              />
            </>
          )}
        </Box>

        {isEditorOrHigher && (
          <Box
            marginTop='m'
            borderWidth={1}
            borderColor='grey02'
            borderRadius='s'
            overflow='hidden'>
            {renderSectionHeader(
              t('projects.members.pending'),
              pendingCount,
              isPendingExpanded,
              () => setIsPendingExpanded(!isPendingExpanded)
            )}
            {isPendingExpanded && pendingCount === 0 && (
              <Box alignItems='center' paddingVertical='l'>
                <Box
                  width={40}
                  height={40}
                  borderRadius='xl'
                  backgroundColor='grey01'
                  alignItems='center'
                  justifyContent='center'>
                  <Icon name='Mail' variant='m' color='textSecondary' />
                </Box>
                <Text
                  marginTop='s'
                  variant='webBodySecondary'
                  color='textPrimary'>
                  {t('shared:noInvitationsSentYet')}
                </Text>
                <Box marginTop='s'>
                  <Button
                    isSmall
                    backgroundColor='black'
                    borderRadius='s'
                    paddingHorizontal='s'
                    prefix={<Icon name='UserPlus' variant='s' color='white' />}
                    accessibilityLabel={t('shared:inviteMembers')}
                    onPress={() =>
                      onInvitePress
                        ? onInvitePress()
                        : setLocalShowAddMembersModal(true)
                    }>
                    <Text variant='webBodySecondary' color='white'>
                      {t('shared:inviteMembers')}
                    </Text>
                  </Button>
                </Box>
              </Box>
            )}
            {isPendingExpanded && pendingCount > 0 && (
              <>
                {getPendingHeader()}
                <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                  <FlatList
                    showsVerticalScrollIndicator={true}
                    data={filteredUserRequests}
                    renderItem={({ item }) => (
                      <PendingInviteRow>
                        <Box flex={1.2} flexDirection='row' alignItems='center'>
                          <Icon
                            name='Mail'
                            color='textPrimary'
                            style={{ width: 16, height: 16 }}
                          />
                          <Text
                            variant='webBodySecondary'
                            color='textPrimary'
                            marginLeft='xs'>
                            {item?.user?.name}
                          </Text>
                        </Box>
                        <Box flex={0.8} flexDirection='row' alignItems='center'>
                          <Text variant='webBodySecondary' color='textPrimary'>
                            {item.invitedBy?.name ||
                              `${item.invitedBy?.firstName || ''} ${
                                item.invitedBy?.lastName || ''
                              }`.trim() ||
                              '-'}
                          </Text>
                        </Box>
                        <Box flex={0.8}>
                          <Text variant='webBodySecondary' color='textPrimary'>
                            {formatDate(item.createdAt)}
                          </Text>
                        </Box>
                        <Box flex={0.8}>
                          <Text variant='webBodySecondary' color='textPrimary'>
                            {formatDate(item.expiredAt)}
                          </Text>
                        </Box>
                        <Box flex={0.5}>
                          <Text variant='webBodySecondary' color='textPrimary'>
                            {formatRole(item.role)}
                          </Text>
                        </Box>
                        <Box
                          flex={0.5}
                          flexDirection='row'
                          justifyContent='center'
                          alignItems='center'>
                          {/* TA-10737: no Resend here. These rows are UserRequests, and a
                              Copy Link signup lands here as a join request awaiting approval,
                              not an invitation. resendProjectInvitation takes a
                              ProjectInvitation id, so passing item.id (a UserRequest id) either
                              fails or resends an unrelated invitation that shares the id.
                              Native already hides it (PendingMemberItem canResend). */}
                          {canCancelInvite && (
                            <TooltipWebV3
                              minWidth='auto'
                              marginOffset={0}
                              title={
                                <Text
                                  numberOfLines={0}
                                  flexShrink={1}
                                  variant='labelSmall'
                                  color='white'>
                                  Cancel Invite
                                </Text>
                              }
                              placement='bottom-end'>
                              <div style={{ flex: 1 }}>
                                <SimpleHoverable
                                  onPress={() =>
                                    handleDeleteInvitation({
                                      id: item.id,
                                      displayName:
                                        item.user?.name ||
                                        `${item.user?.firstName ?? ''} ${
                                          item.user?.lastName ?? ''
                                        }`.trim() ||
                                        item.user?.email ||
                                        '',
                                      type: 'userRequest',
                                    })
                                  }>
                                  {(hover) => (
                                    <Box
                                      padding='xs'
                                      borderRadius='xs'
                                      backgroundColor={
                                        hover ? 'grey02' : undefined
                                      }
                                      alignItems='center'
                                      justifyContent='center'>
                                      <Icon
                                        color={hover ? 'alertRed' : 'grey05'}
                                        variant='s'
                                        name='Trash2'
                                        marginHorizontal='none'
                                      />
                                    </Box>
                                  )}
                                </SimpleHoverable>
                              </div>
                            </TooltipWebV3>
                          )}
                        </Box>
                      </PendingInviteRow>
                    )}
                    keyExtractor={(item) => item.id}
                    ItemSeparatorComponent={() => (
                      <Box height={1} backgroundColor='grey02' />
                    )}
                  />
                  <FlatList
                    showsVerticalScrollIndicator={true}
                    data={filteredInvitations}
                    renderItem={({ item }) => (
                      <PendingInviteRow>
                        <Box flex={1.2} flexDirection='row' alignItems='center'>
                          <Icon
                            name='Mail'
                            color='textPrimary'
                            style={{ width: 16, height: 16 }}
                          />
                          <Text
                            variant='webBodySecondary'
                            color='textPrimary'
                            marginLeft='xs'>
                            {item.email}
                          </Text>
                        </Box>
                        <Box flex={0.8} flexDirection='row' alignItems='center'>
                          <Text variant='webBodySecondary' color='textPrimary'>
                            {item.invitedBy?.name ||
                              `${item.invitedBy?.firstName || ''} ${
                                item.invitedBy?.lastName || ''
                              }`.trim() ||
                              '-'}
                          </Text>
                        </Box>
                        <Box flex={0.8}>
                          <Text variant='webBodySecondary' color='textPrimary'>
                            {formatDate(item.createdAt)}
                          </Text>
                        </Box>
                        <Box flex={0.8}>
                          <Text variant='webBodySecondary' color='textPrimary'>
                            {formatDate(item.expiredAt)}
                          </Text>
                        </Box>
                        <Box flex={0.5}>
                          <Text variant='webBodySecondary' color='textPrimary'>
                            {formatRole(item.role)}
                          </Text>
                        </Box>
                        <Box
                          flex={0.5}
                          flexDirection='row'
                          justifyContent='center'
                          alignItems='center'>
                          <TooltipWebV3
                            minWidth='auto'
                            marginOffset={0}
                            title={
                              <Text
                                numberOfLines={0}
                                flexShrink={1}
                                variant='labelSmall'
                                color='white'>
                                Resend Invitation
                              </Text>
                            }
                            placement='bottom-end'>
                            <div style={{ flex: 1 }}>
                              <SimpleHoverable
                                onPress={() => handleResendInvitation(item.id)}>
                                {(hover) => (
                                  <Box
                                    padding='xs'
                                    borderRadius='xs'
                                    backgroundColor={
                                      hover ? 'grey02' : undefined
                                    }
                                    alignItems='center'
                                    justifyContent='center'
                                    marginRight='xs'>
                                    <Icon
                                      color='grey05'
                                      variant='s'
                                      name='Repeat'
                                      marginHorizontal='none'
                                    />
                                  </Box>
                                )}
                              </SimpleHoverable>
                            </div>
                          </TooltipWebV3>
                          {canCancelInvite && (
                            <TooltipWebV3
                              minWidth='auto'
                              marginOffset={0}
                              title={
                                <Text
                                  numberOfLines={0}
                                  flexShrink={1}
                                  variant='labelSmall'
                                  color='white'>
                                  Cancel Invite
                                </Text>
                              }
                              placement='bottom-end'>
                              <div style={{ flex: 1 }}>
                                <SimpleHoverable
                                  onPress={() =>
                                    handleDeleteInvitation({
                                      id: item.id,
                                      displayName: item.email,
                                      type: 'projectInvitation',
                                    })
                                  }>
                                  {(hover) => (
                                    <Box
                                      padding='xs'
                                      borderRadius='xs'
                                      backgroundColor={
                                        hover ? 'grey02' : undefined
                                      }
                                      alignItems='center'
                                      justifyContent='center'>
                                      <Icon
                                        color={hover ? 'alertRed' : 'grey05'}
                                        variant='s'
                                        name='Trash2'
                                        marginHorizontal='none'
                                      />
                                    </Box>
                                  )}
                                </SimpleHoverable>
                              </div>
                            </TooltipWebV3>
                          )}
                        </Box>
                      </PendingInviteRow>
                    )}
                    keyExtractor={(item) => item.id}
                    ItemSeparatorComponent={() => (
                      <Box height={1} backgroundColor='grey02' />
                    )}
                  />
                </div>
              </>
            )}
          </Box>
        )}
      </ScrollView>

      {showAddMembersModal && (
        <WebModalV3
          canOverflowModal={true}
          visible={showAddMembersModal}
          onClose={handleCloseModal}
          width={800}
          height={412}>
          <ProjectInviteOrAddMember
            projectId={projectUUId}
            canAssignAdminRole={canUpdateAdminRole(
              project?.currentUserRole,
              project?.currentUserTeamRole
            )}
            treatInitialSelectionsAsChanges={
              (initialInviteEmails?.length ?? 0) > 0
            }
            onAddEmailInvitations={async (
              invitations,
              _selectedUsers,
              hasExternalUsers
            ) => {
              if (project?.id && invitations.length > 0) {
                try {
                  await createProjectInvitation({
                    variables: {
                      projectId: project.id,
                      projectMembers: invitations,
                    },
                  });
                } catch {
                  // Error is handled by Apollo Client
                }
                const toastTitle = hasExternalUsers
                  ? t('projects.members.invitationSent')
                  : t('projects.members.memberAdded');
                if (onInviteSent) {
                  onInviteSent();
                } else {
                  toast.show(
                    <FileDeletedToast
                      title={toastTitle}
                      duration={3000}
                      width={240}
                    />,
                    {
                      type: 'custom',
                      duration: 3000,
                      placement: 'bottom',
                      animationType: 'slide-in',
                      style: {
                        backgroundColor: 'transparent',
                        shadowColor: 'transparent',
                        padding: 0,
                        margin: 0,
                        width: 240,
                        height: 80,
                      },
                    }
                  );
                }
              }
            }}
            selectedUser={
              initialInviteEmails?.map((assignee) =>
                assignee.id
                  ? ({
                      id: assignee.id,
                      name: assignee.name ?? assignee.email,
                      email: assignee.email,
                      avatar: assignee.avatarId
                        ? { id: assignee.avatarId }
                        : undefined,
                      role: ProjectMemberRole.Viewer,
                      __typename: 'User' as const,
                    } as AddMemberType)
                  : ({
                      id: assignee.email,
                      name: assignee.email,
                      email: assignee.email,
                      role: ProjectMemberRole.Viewer,
                      __typename: 'InvitedUser' as const,
                    } as AddMemberType)
              ) ?? []
            }
            existingProjectMembers={[
              ...(projectMembers ?? []).map((m) => ({
                ...m.user,
                role: m.role as ProjectMemberRole,
                __typename: 'User' as const,
              })),
              ...(projectInvitations ?? []).map((inv, index) => ({
                id: `invited-${index}-${inv.email}`,
                email: inv.email,
                name: inv.email,
                role:
                  (inv.role as ProjectMemberRole) ?? ProjectMemberRole.Viewer,
                __typename: 'InvitedUser' as const,
              })),
              ...(projectUsers ?? []).map((ur) => ({
                ...ur.user,
                role:
                  (ur.role as ProjectMemberRole) ?? ProjectMemberRole.Viewer,
                __typename: 'User' as const,
              })),
            ]}
            onClose={handleCloseModal}
          />
        </WebModalV3>
      )}

      <CancelInviteMemberConfirmModal
        showModal={showDeleteInviteModal}
        isLoading={isCancelingInvite}
        onClose={() => {
          setShowDeleteInviteModal(false);
          setInvitationToDelete(null);
        }}
        onPress={confirmDeleteInvitation}
        showBold={false}
        title={t('projects.members.cancelInvite')}
        message={t('projects.members.cancelInviteMessage')}
        memberName={invitationToDelete?.displayName || ''}
        buttonText={t('projects.members.cancelInvite')}
        isAlert={true}
      />
    </>
  );
};
